import { useState } from "react";
import { Link } from "react-router";
import { Modal } from "../components/Modal";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Table,
  TableSkeleton,
  Tag,
  Td,
  Th,
  buttonStyles,
  cx,
} from "../components/ui";
import { formatDateTime, formatRelative } from "../lib/format";
import { useAnnouncements, useDeleteAnnouncement, useUpdateAnnouncement } from "../lib/queries";
import type { Announcement, AnnouncementAudience, AnnouncementKind } from "../lib/types";

const LIMIT = 25;

/**
 * What the dashboard and the public site are telling customers.
 *
 * The list shows every row including drafts and expired ones, because this is
 * where a maintenance notice is written before it runs and where last month's
 * incident gets looked up — both invisible in a list filtered to what a customer
 * can see.
 *
 * Publish and retract are inline links rather than fields on a form. Both are
 * one-click decisions taken under time pressure: an operator who has just fixed
 * an outage should not have to open an editor to stop telling customers it is
 * broken. Writing is a route — see AnnouncementFormPage.
 */
export function AnnouncementsPage() {
  const [offset, setOffset] = useState(0);
  const { data, isPending, error } = useAnnouncements({ limit: LIMIT, offset });

  const [confirmDelete, setConfirmDelete] = useState<Announcement | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const update = useUpdateAnnouncement();
  const remove = useDeleteAnnouncement();

  function togglePublished(row: Announcement) {
    setRowError(null);
    setPendingId(row.id);
    update
      .mutateAsync({ id: row.id, input: { published: !row.published } })
      .catch((cause: Error) => setRowError(cause.message))
      .finally(() => setPendingId(null));
  }

  return (
    <>
      <PageHeader
        title="Announcements"
        description="What the dashboard and the public site are telling customers right now."
        action={
          <Link to="/announcements/new" className={buttonStyles("primary", "md")}>
            New announcement
          </Link>
        }
      />

      {rowError && (
        <div className="mb-4">
          <Alert>{rowError}</Alert>
        </div>
      )}

      <Card>
        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            icon="announcements"
            title="Nothing published"
            description="Neither the dashboard nor the public site is showing a notice."
          />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>Notice</Th>
                  <Th priority="sm">Showing</Th>
                  <Th priority="md">Where</Th>
                  <Th priority="lg">Window</Th>
                  <Th className="text-right"> </Th>
                </tr>
              }
            >
              {data.data.map((row) => (
                <tr key={row.id} className="hover:bg-raised">
                  <Td>
                    {/*
                      The dot and the kind, in the same vocabulary both customer
                      surfaces use — so a row here reads as the thing it will
                      become rather than as a database record about it.
                    */}
                    <div className="flex items-center gap-2">
                      <span
                        className={cx(
                          "dot-status",
                          kindMark(row.kind),
                          row.kind === "incident" && "dot-status-pulse",
                        )}
                        aria-hidden="true"
                      />
                      <Link
                        to={`/announcements/${row.id}`}
                        className="max-w-md truncate font-medium text-fg hover:text-accent"
                      >
                        {row.title}
                      </Link>
                      <KindTag kind={row.kind} />
                    </div>
                    <p className="mt-0.5 max-w-md truncate text-xs text-fg-subtle">{row.body}</p>
                  </Td>
                  <Td priority="sm">
                    <LiveState row={row} />
                  </Td>
                  <Td priority="md">
                    <span className="text-fg-muted">{audienceLabel(row.audience)}</span>
                    {/* The scope, when there is one. A notice aimed at one
                        server reads very differently from a platform-wide one,
                        and the difference must be visible without opening it. */}
                    {row.serverId && (
                      <p className="mt-0.5 text-xs text-fg-subtle">
                        {row.serverName ?? "one server"} only
                      </p>
                    )}
                  </Td>
                  <Td priority="lg" className="whitespace-nowrap text-fg-subtle">
                    <p title={formatDateTime(row.startsAt)}>{formatRelative(row.startsAt)}</p>
                    <p className="text-xs">
                      {row.endsAt ? `until ${formatDateTime(row.endsAt)}` : "no end set"}
                    </p>
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    <button
                      type="button"
                      disabled={pendingId === row.id}
                      onClick={() => togglePublished(row)}
                      className="text-sm text-accent hover:underline disabled:opacity-50"
                    >
                      {pendingId === row.id
                        ? "Saving…"
                        : row.published
                          ? "Retract"
                          : "Publish"}
                    </button>
                    <Link
                      to={`/announcements/${row.id}`}
                      className="ml-3 text-sm text-accent hover:underline"
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(row)}
                      className="ml-3 text-sm text-danger hover:underline"
                    >
                      Delete
                    </button>
                  </Td>
                </tr>
              ))}
            </Table>

            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>

      <p className="px-1 text-xs leading-6 text-fg-subtle">
        A published notice reaches readers within a minute — the public endpoint is cached for that
        long. Retracting takes the same minute. Deleting removes the record of what was said; retract
        it instead if you may need to explain it later.
      </p>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete this announcement?"
        description="The audit log keeps a record of the title. Nothing else survives."
        dismissable={!remove.isPending}
      >
        <p className="text-sm text-fg-muted">
          {confirmDelete?.published
            ? "This notice is live. Deleting it stops it showing, but so does retracting it — and retracting keeps what you said."
            : "This notice is not showing anywhere."}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => {
              if (!confirmDelete) return;
              setRowError(null);
              remove
                .mutateAsync(confirmDelete.id)
                .then(() => setConfirmDelete(null))
                .catch((cause: Error) => {
                  setRowError(cause.message);
                  setConfirmDelete(null);
                });
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}

/**
 * The dot's colour, matching what the customer will see.
 *
 * `warn` and `danger` mean the same thing in every surface here, so maintenance
 * and incident are faithful. Info and a promotion both take this console's amber
 * where a customer sees blue — copying the customer accent in to make it exact is
 * explicitly not allowed, because that accent is the only thing distinguishing
 * the two consoles at a glance.
 */
function kindMark(kind: AnnouncementKind): string {
  if (kind === "incident") return "text-danger";
  if (kind === "maintenance") return "text-warn";
  return "text-accent";
}

function KindTag({ kind }: { kind: AnnouncementKind }) {
  /*
   * `promo` and `info` share the accent tone here because they share the hue out
   * there — the banners separate them by weight, solid against tint, which a
   * 20px tag has no room to express.
   */
  const tone = kind === "incident" ? "bad" : kind === "maintenance" ? "warn" : "accent";
  return <Tag tone={tone}>{kind}</Tag>;
}

/**
 * Whether this notice is on a screen right now, which is not the same question
 * as whether it is published.
 *
 * A published notice can be scheduled for tomorrow or expired last week, and an
 * operator scanning this column wants the live ones — so the state is computed
 * from the window rather than read off the flag. The comparison is against the
 * browser's clock while the API filters on the database's; a few seconds of
 * disagreement at a boundary is not worth a round trip to resolve.
 */
function LiveState({ row }: { row: Announcement }) {
  if (!row.published) return <Tag>draft</Tag>;

  const now = Date.now();
  if (new Date(row.startsAt).getTime() > now) return <Tag tone="accent">scheduled</Tag>;
  if (row.endsAt && new Date(row.endsAt).getTime() <= now) return <Tag>ended</Tag>;
  return <Tag tone="good">live</Tag>;
}

function audienceLabel(audience: AnnouncementAudience): string {
  if (audience === "both") return "Dashboard + site";
  if (audience === "dashboard") return "Dashboard";
  return "Public site";
}
